// Sugalarm — cycle 398: the in-range DISPLAY stand-down predicate, and the
// audio derivation that hangs off it.
//
// THE OPERATOR RULE (verbatim intent): "as soon as a reading is back to In
// Range, the previous banner needs to go away instantly. It does not matter
// if it's coming from a high or a low."
//
// Cycle 304 introduced `bannerSuppressedInRange` but scoped it to MUTED
// episodes only (snoozed / silenced / stopped). That exclusion existed to
// keep the cycle-81 control floor — "at least one alarm control visible
// whenever the audio loop could be sounding" — true by construction:
//
//     cycle 304:   audioOn  ⇒  ¬standDown
//
// which held because an ACTIVE episode could never satisfy the predicate.
// The cost of that guarantee was the reported bug: an ACTIVE low/high left
// its banner (and, on urgent, its full-screen takeover) on the tablet and
// the phone for minutes after the household was already back in range.
//
// ⚠ THIS CYCLE DOES NOT WEAKEN THE FLOOR. IT RE-DERIVES IT FROM THE OTHER
// SIDE, AND THE PROPOSITION IS LITERALLY THE SAME ONE:
//
//     cycle 398:   standDown  ⇒  ¬audioOn
//
// Both are `¬(audioOn ∧ standDown)`. What changes is which term is computed
// FIRST. `standDown` is now derived from the reading alone and `audioOn` is
// derived FROM it (`audioOn = active && !silenced && !treating &&
// !standDown`), so the audio stops in the SAME RENDER the banner hides.
// There is no frame in which the loop can sound with no control on screen —
// which is what the floor actually asks for.
//
// ⚠ RE-ARM IS FREE. Nothing here ends, clears, resets or acknowledges an
// episode. `phase`, the snooze countdown, `startLevel`, the bound alert row
// and the server's authority over `recovered_at` are all untouched. The very
// next alarm-side reading flips `standDown` false and the banner AND the
// audio return in the same render, with no new row and no server round trip.
//
// Extracted as a pure function so the hook and its tests read the SAME
// derivation — a golden-vector test that re-implements the rule proves
// nothing about the code that ships.

import type { AlertLevel } from "./types";

/** Everything the stand-down decision is allowed to look at. Deliberately
 *  a flat value object: no clocks, no fetches, no refs — so the property
 *  ("standDown ⇒ ¬audioOn") is testable without a renderer. */
export interface StandDownInput {
  /** The episode is live in ANY phase — active, snoozed, silenced/stopped,
   *  or inside a cycle-391 treating window. The cycle-304 "muted only"
   *  restriction is what this cycle removes. */
  isLive: boolean;
  /** TestAlarmDriver / PreviewOverlay synthetic episode. A synthetic
   *  episode is NEVER driven by real readings, so it can never stand down
   *  from one — previews behave exactly as they did before this cycle. */
  synthetic: boolean;
  /** There is a resolvable active member. Mirrors the episode's own
   *  cycle-310 creation guard and the cycle-231 audio arm gate: with no
   *  member context the top-level reading may belong to a FALLBACK member,
   *  and another member's number must never stand down this alarm. */
  hasActiveMember: boolean;
  /** A latest reading exists at all. */
  hasReading: boolean;
  /** The latest reading's level under the member's CURRENT effective
   *  thresholds, re-derived every render. `null` means in range.
   *
   *  Re-deriving (rather than latching) is what makes the "even after the
   *  threshold changes" half of the report work for free: editing a
   *  threshold in Settings so the newest reading is now in range clears the
   *  banner in the same render, with no extra code path. */
  readingLevel: AlertLevel | null;
  /** The latest reading is fresh by the EXISTING staleness rule (the same
   *  `isStale` the rest of the client uses — no new threshold is invented
   *  here). Stale is NEUTRAL: never in range, never a stand-down. */
  readingFresh: boolean;
  /** Epoch ms of the latest reading's own `timestamp` (not received_at). */
  readingTsMs: number | null;
  /** Epoch ms of the reading that ARMED or last RE-ARMED this episode, or
   *  null when unknown.
   *
   *  ⚠ THIS IS THE CONDITION THAT KEEPS A LIVE ALARM ALIVE. A connect
   *  snapshot, a reconnect replay, or a backfilled older row can all move
   *  `lastReading` BACKWARD to an older, in-range value while a genuine low
   *  is happening. Requiring the reading to be STRICTLY NEWER than the one
   *  that armed the episode means only forward progress can stand an alarm
   *  down. Unknown (`null`) cannot be treated as "arbitrarily old" — see
   *  `inRangeStandDown`. */
  armedReadingTsMs: number | null;
}

/** ⚠ CYCLE 428: THE EARLY STAND-DOWN IS REMOVED. This returns `false` for
 *  every live episode, by design, and the input shape is kept only so the
 *  audio derivation (`audioOnFor`) and its invariant are byte-identical.
 *
 *  Cycle 398 stood the display down on the FIRST fresh in-range reading —
 *  one reading before the server's two-consecutive-fresh recovery. That was
 *  a DEVICE deciding an alarm is over (v3 must-never-happen #1: only the
 *  server recovers an episode), and an artifact in-range reading clearing
 *  the screen then the next reading bringing it back was the flicker the
 *  household reported. The display now clears ONLY on the server's recovery
 *  frame: `recovered_at` on the bound row ends the episode (the cycle-82
 *  effect in `useAlarmEpisode`), `isLive` goes false, and every alarm
 *  surface unmounts on its own `isLive` gate — together, on every device.
 *
 *  Through the recovering window (first in-range reading → recovery) the
 *  takeover keeps SHOWING and the loop keeps SOUNDING (operator decision
 *  2026-09-08, option 1; v3 "nobody responds": the kiosk sounds until a
 *  human takes responsibility or the server recovers). The header says why
 *  — see `recoveringFromUrgentLow`. */
export function inRangeStandDown(_s: StandDownInput): boolean {
  return false;
}

/** Inputs to the Stage-6 header rule. Flat, clock-free, no refs. */
export interface RecoveringInput {
  /** The episode is live (open on the server, not yet recovered). */
  isLive: boolean;
  /** The episode's effective level (`episodeLevel`). */
  episodeLevel: AlertLevel | null;
  /** The latest reading's value, or null. */
  sgv: number | null;
  /** The latest reading is fresh by the existing `isStale` rule. */
  readingFresh: boolean;
  /** The member's URGENT-LOW line (`urgent_low`). ABOVE it the member is
   *  climbing out of the urgent low — the whole climb, band included, and
   *  everything above (a high-side reading counts too). The line itself is
   *  exclusive, matching the engine (`_level_for` fires urgent BELOW it). */
  urgentLow: number;
}

/** Cycle 428 — v3 "Coming back up": the takeover and treating-screen
 *  HEADER reads "Recovering from an urgent low, low alerts are held until
 *  [member] is back in range" while the urgent-low row is still open and the
 *  latest fresh reading is ABOVE the urgent-low line — the band between the
 *  two lines (where the low rung is latched and the calls are held) and
 *  everything above it, until the SERVER recovers. TEXT ONLY: the ring, the
 *  buttons and the sound state are exactly what they were the reading
 *  before. Stale is neutral (a dark sensor is not "recovering"). */
export function recoveringFromUrgentLow(s: RecoveringInput): boolean {
  if (!s.isLive) return false;
  if (s.episodeLevel !== "urgent_low") return false;
  if (s.sgv == null) return false;
  if (!s.readingFresh) return false;
  return s.sgv > s.urgentLow;
}

/** The v3 Stage-6 sentence, in one place for the takeover and the treating
 *  screen. Names the member when known. No em dash (house rule). */
export function recoveringHeaderLine(memberName: string | null | undefined): string {
  const name = memberName?.trim();
  return name
    ? `Recovering from an urgent low, low alerts are held until ${name} is back in range`
    : "Recovering from an urgent low, low alerts are held until back in range";
}

/** Inputs to the audio derivation. Separate from `StandDownInput` because
 *  audio is a FUNCTION of the stand-down, never the other way round. */
export interface AudioOnInput {
  phase: "idle" | "active" | "snoozed";
  /** Stop pressed (cycle 66: `silenced` and `stopped` are one flag). */
  silenced: boolean;
  /** Cycle 391 treating window open. RETAINED deliberately — see below. */
  treating: boolean;
  /** The stand-down predicate for this same render. Since cycle 428 it
   *  is `false` for every live episode (the early stand-down is gone); the
   *  term is kept so `standDown ⇒ ¬audioOn` is unchanged by construction. */
  standDown: boolean;
}

/** `audioOn = phase === "active" && !silenced && !treating && !standDown`.
 *
 *  ⚠ `!treating` IS NOT AN EXTRA THIS CYCLE ADDED — it is the cycle-391
 *  term, kept. Dropping it would restart the alarm loop over an open
 *  treating window, which is a live-alarm regression and is on this cycle's
 *  out-of-scope list. Adding `!standDown` in front of it is what makes
 *  `standDown ⇒ ¬audioOn` true by construction. */
export function audioOnFor(s: AudioOnInput): boolean {
  return (
    s.phase === "active" && !s.silenced && !s.treating && !s.standDown
  );
}
